/**
 * Feature E03-F08-T01 (FP-05) — "Generation: Approve и назначение команды" —
 * PERSISTED sourcing-team assignment on a project.
 *
 * The Generation stage lets a user assign a SOURCING team (`sourcing_teams`:
 * name + members JSONB — the admin-sourcing-teams domain) to a project. Until
 * now the only picker was the EPHEMERAL email-recipient picker in
 * SendEmailDialog: it filled the email "To:" field and then discarded the team —
 * nothing was stored, so the assignment did not survive a reload. This contract
 * backs a real, persisted assignment stored on `projects.sourcing_team_id`
 * (schema.prisma → Project.sourcingTeamId, migration
 * 20260708130000_add_project_sourcing_team), independent of sending any email.
 *
 * NOTE: this is the SOURCING team (`sourcing_teams`), NOT the recruiter `teams`
 * table / `users.team_id`. Do not conflate them.
 *
 * Endpoint the backend route must honour (routes/*, implemented by
 * backend-engineer — NOT here):
 *   PATCH /projects/:id/sourcing-team
 *     body     → { sourcingTeamId: number | null }   (null = un-assign)
 *     response → { success:true, project_id, sourcing_team_id, sourcing_team }
 *
 * IDOR invariant (carried over from projectMutations.ts): the project `:id` is
 * scoped to the caller's session on the api side; no owner/user field is ever
 * read from this body.
 *
 * This file owns its own symbols and is re-exported from
 * packages/shared/src/index.ts so BOTH @eer/api (route validation) and @eer/web
 * (typed client) import the SAME shapes.
 */
import { z } from 'zod';
import { sourcingTeamMemberSchema } from './admin-sourcing-teams.js';
// ---------------------------------------------------------------------------
// Resolved team reference (embedded in the project response for convenience)
// ---------------------------------------------------------------------------
/**
 * A lightweight, resolved view of the sourcing team currently assigned to a
 * project — enough for the UI to render the assignment without a second fetch.
 *
 * Deliberately does NOT carry `is_active` (unlike {@link sourcingTeamSchema} in
 * admin-sourcing-teams): a team may be SOFT-deleted (`is_active=false`) yet still
 * be the assigned team on an older project, and the card must still show it.
 */
export const projectSourcingTeamRefSchema = z.object({
    id: z.number().int(),
    name: z.string(),
    /** JSONB array of { name, email } (same member shape as admin-sourcing-teams). */
    members: z.array(sourcingTeamMemberSchema),
});
// ---------------------------------------------------------------------------
// PATCH /projects/:id/sourcing-team — request
// ---------------------------------------------------------------------------
/**
 * Assign (or, with `null`, un-assign) the sourcing team for a project. `null`
 * clears `projects.sourcing_team_id`. A numeric id must reference an existing
 * `sourcing_teams` row (the route enforces existence + IDOR scope). `z.coerce`
 * tolerates a stringified number from the REST client while `.nullable()`
 * short-circuits an explicit `null` before coercion.
 */
export const assignSourcingTeamBodySchema = z.object({
    sourcingTeamId: z.coerce.number().int().positive().nullable(),
});
// ---------------------------------------------------------------------------
// PATCH /projects/:id/sourcing-team — response
// ---------------------------------------------------------------------------
/**
 * The new assignment after a successful PATCH. `sourcing_team_id` is the stored
 * FK value (null when un-assigned); `sourcing_team` is the resolved team (null
 * when un-assigned) so the client can update its view without a refetch.
 */
export const assignSourcingTeamResponseSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    project_id: z.number().int(),
    sourcing_team_id: z.number().int().nullable(),
    sourcing_team: projectSourcingTeamRefSchema.nullable(),
});
//# sourceMappingURL=project-sourcing-team.js.map